import { useQueryClient } from "@tanstack/react-query";
import { useAppMutation } from "../../../../../../../../http/useAppMutation.ts";
import { trackEvent } from "../../../../../../../../tracking/tracking.ts";
import { variationSettingsStorage } from "../../../../../../../boardState/stores/settings/variationSettingsStorage.ts";
import { useWorkspaceUuid } from "../../../../../../../hooks/useWorkspaceUuid.ts";
import { LORA_GENERATION_TYPE } from "../../../../../../constants.ts";
import { useBoardUuid } from "../../../../../../hooks/useBoardUuid.ts";
import { useImageVariationSettings } from "./useImageVariationSettings.ts";

export const useGenerateImageVariation = ({
  onSuccess,
}: {
  onSuccess?: () => void;
}) => {
  const workspaceUuid = useWorkspaceUuid();
  const boardUuid = useBoardUuid();
  const variationSettings = variationSettingsStorage.useSettings(boardUuid);

  const generateImageVariationMutation = useAppMutation({
    path: "boards/generate-image-variation",
    invalidate: [
      `workspaces/${workspaceUuid}/boards`,
      `boards/${boardUuid}/assets`,
    ],
    onSuccess: () => {
      trackEvent(
        "content:generate",
        {
          generation_type: "Variation",
        },
        true,
      );
    },
  }).mutation;

  const { settings } = useImageVariationSettings(boardUuid);

  const selectedElementGenerationParams =
    settings.selectedModels.generationParams.length > 0
      ? settings.selectedModels.generationParams[0]
      : null;

  const queryClient = useQueryClient();
  return {
    mutation: ({ assetUuid }: { assetUuid: string }) => {
      generateImageVariationMutation.mutate(
        {
          board_uuid: boardUuid,
          asset_uuid: assetUuid,
          prompt: variationSettings.prompt,
          num_generations: variationSettings.num_generations,
          prompt_strength:
            variationSettings.creativity_strength_preset === "CUSTOM"
              ? variationSettings.prompt_strength
              : undefined,
          creativity_strength_preset:
            variationSettings.creativity_strength_preset,
          guidance_scale: variationSettings.guidance_scale,
          ...(selectedElementGenerationParams &&
            selectedElementGenerationParams.model_generation_type ===
              LORA_GENERATION_TYPE && {
              reference_uuid: selectedElementGenerationParams.uuid,
              lora_scale: selectedElementGenerationParams.scale,
            }),
        },
        {
          onSuccess: () => {
            [`assets/${assetUuid}`, `assets/${assetUuid}/history`].map(
              (queryKey) =>
                void queryClient.invalidateQueries({ queryKey: [queryKey] }),
            );
            onSuccess?.();
          },
        },
      );
    },
    isLoading: generateImageVariationMutation.isLoading,
  };
};
